import type { Probe, ProbeResult } from '../engine/types';
import { ok, na } from '../engine/runner';
import { registerProbe } from '../engine/registry';
import { sig } from '../engine/normalize';

const FONTS_SOURCE = 'https://w3c.github.io/webappsec-privacy-considerations/';

/**
 * Deep font enumeration.
 *
 * The classic technique: measure a probe string in a candidate font and in a
 * deliberate fallback, and mark the font "available" when the widths differ.
 * This is shallow compared to full glyph enumeration, but the set of installed
 * fonts is a genuinely high-entropy, stable signal — and a Linux container
 * exposes a very different set from a real Windows or macOS machine, which
 * makes it a coherence target against the claimed OS.
 *
 * We combine it with the shaping-metrics probe (fonts.ts) which measures HOW
 * the text is shaped. Together they cover what the reference site does with its
 * `fonts` signal.
 */
export const fontEnumerateProbe: Probe = {
  id: 'fontEnumerate',
  label: 'Font enumeration',
  layer: 'gpu',
  tier: 'client',
  category: 'hardware',
  description: 'Installed font detection via width-difference probe',
  sources: [FONTS_SOURCE],
  budgetMs: 3000,

  async run(): Promise<ProbeResult> {
    const ctx = document.createElement('canvas');
    const g = ctx.getContext('2d', { willReadFrequently: true });
    if (!g) {
      return { signals: { available: na<boolean>('unavailable', 'client', 'no 2d context') } };
    }

    const probeText = 'mmmmmmmmmmlliWWWW@#$%';
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      // Windows
      'Arial', 'Calibri', 'Cambria', 'Candara', 'Consolas', 'Corbel', 'Courier New',
      'Georgia', 'Impact', 'Segoe UI', 'Tahoma', 'Times New Roman', 'Trebuchet MS', 'Verdana',
      // macOS
      'Avenir', 'Avenir Next', 'American Typewriter', 'Futura', 'Geneva', 'Helvetica',
      'Helvetica Neue', 'Lucida Grande', 'Menlo', 'Monaco', 'Optima', 'Palatino', 'Zapfino',
      // Linux / common
      'DejaVu Sans', 'DejaVu Serif', 'Liberation Sans', 'Liberation Serif', 'FreeSans',
      'Noto Sans', 'Ubuntu', 'Cantarell', 'WenQuanYi Micro Hei',
      // Office / distinctive
      'Gabriola', 'Inconsolata', 'Courier Prime',
    ];

    // Measure each candidate against a guaranteed-different baseline for all
    // three base families; available if ANY base family shows a width delta.
    const available: Record<string, boolean> = {};
    for (const font of testFonts) {
      let detected = false;
      for (const base of baseFonts) {
        g.font = `72px ${font}, ${base}`;
        const withFont = g.measureText(probeText).width;
        g.font = `72px ${base}`;
        const withBase = g.measureText(probeText).width;
        if (sig(withFont, 3) !== sig(withBase, 3)) {
          detected = true;
          break;
        }
      }
      available[font] = detected;
    }

    const present = Object.entries(available)
      .filter(([, v]) => v)
      .map(([k]) => k)
      .sort();

    // Which "signature" fonts exist? These are the most OS-discriminating.
    const windowsOnly = ['Calibri', 'Cambria', 'Candara', 'Corbel', 'Consolas', 'Segoe UI', 'Tahoma', 'Trebuchet MS'].filter((f) => available[f]);
    const macOnly = ['Avenir', 'Avenir Next', 'American Typewriter', 'Futura', 'Geneva', 'Helvetica', 'Helvetica Neue', 'Lucida Grande', 'Menlo', 'Monaco', 'Optima', 'Palatino', 'Zapfino', 'Gabriola'].filter((f) => available[f]);
    const linuxOnly = ['DejaVu Sans', 'DejaVu Serif', 'Liberation Sans', 'Liberation Serif', 'FreeSans', 'Noto Sans', 'Ubuntu', 'Cantarell', 'WenQuanYi Micro Hei'].filter((f) => available[f]);

    return {
      signals: {
        available: ok(true, 'client'),
        presentCount: ok(present.length, 'client'),
        presentFonts: ok(present, 'client'),
        allFonts: ok(available, 'client'),
        // Coarse OS-family fingerprints from the installed font set.
        windowsSignatureCount: ok(windowsOnly.length, 'derived'),
        macSignatureCount: ok(macOnly.length, 'derived'),
        linuxSignatureCount: ok(linuxOnly.length, 'derived'),
        impliedOs: ok(
          macOnly.length > windowsOnly.length && macOnly.length > linuxOnly.length
            ? 'macOS'
            : windowsOnly.length > linuxOnly.length
              ? 'Windows'
              : linuxOnly.length > 0
                ? 'Linux'
                : 'unknown',
          'derived',
        ),
      },
      parts: {
        fonts: {
          // The presence set is high-entropy and stable per device, so it is a
          // genuine fingerprint component.
          presentCount: present.length,
          presentFonts: present,
        },
      },
    };
  },
};

registerProbe(fontEnumerateProbe);
